import _ from 'lodash';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useForm from 'shared/hooks/useForm';
import { buildSeoTitle, buildMetaDescription } from 'shared/lib/seoHelpers';
import { isStringValid, isEmailValid } from 'shared/lib/validators';
import Alert from 'shared/components/Alert';
import { Input, Submit } from 'shared/components/Form';
import Subheader from 'shared/components/Subheader';
import { BLOG_URL, FACEBOOK_URL, LINKEDIN_URL, createUserportalLink, userportal as routes } from 'shared/routes';
import { selectors as authSelectors } from 'userportal/ducks/auth';
import { selectors as profileSelectors } from 'userportal/ducks/profile';
import Page from 'userportal/components/Page';
import apiClient from 'userportal/lib/ApiClient';
import styles from './Contact.module.scss';

const Contact = () => {
  const [state, setState] = useState({ processing: false, sent: false, error: false });
  const { t } = useTranslation('common');

  const user = useSelector(authSelectors.getUser);
  const profile = useSelector(profileSelectors.getUserProfile);

  const onSubmit = async (data) => {
    setState({ processing: true, sent: false, error: false });
    try {
      await apiClient.post('/contact', {
        form: data,
        sentByUser: !_.isEmpty(user) ? {
          id: user.id,
          firstname: user.firstname,
          lastname: user.lastname,
          email: user.email
        } : undefined,
      });
      setState({ processing: false, sent: true, error: false });
    } catch {
      setState({ processing: false, sent: false, error: true });
    }
  };

  const validate = (data) => {
    const errors = {};

    if (!isStringValid(data.name)) {
      errors.name = t('validation.fillFullName');
    }

    if (!isEmailValid(data.email)) {
      errors.email = t('validation.fillValidEmail');
    }

    if (!isStringValid(data.note)) {
      errors.note = t('page.contact.fillContent');
    }

    return errors;
  };

  const { values, errors, hasError, handleChange, handleSubmit } = useForm(onSubmit, {
    name: !_.isEmpty(user) ? `${user.firstname} ${user.lastname}` : '',
    phone: !_.isEmpty(profile) ? profile.phone : '',
    email: !_.isEmpty(user) ? user.email : '',
    note: ''
  }, validate);

  return (
    <Page
      initialRow={false}
      className={styles.page}
      subheader={(
        <Subheader center elementType="h1" title={t('page.contact.contact')}>
          <span>{t('page.contact.subtitle')}</span>
        </Subheader>
      )}
      helmetTitle={buildSeoTitle(t, t('page.contact.contact'))}
      helmetDescription={buildMetaDescription(t('userportal:page.contact.p1'))}
      seoUrl={createUserportalLink(routes.contact)}
    >
      <div className={styles.intro}>
        <p>{t('userportal:page.contact.p1')}</p>
        <div className={styles.topics}>
          <span className={styles.topicsLabel}>{t('userportal:page.contact.topicsLabel')}</span>
          {[1, 2, 3, 4, 5].map(n => (
            <span key={n} className={styles.topic}>{t(`userportal:page.contact.topic${n}`)}</span>
          ))}
        </div>
      </div>
      <div id="form" className={styles.grid}>
        <div className={styles.formCard}>
          {state.error && (
            <Alert variant="error">{t('page.contact.requestFailed')}</Alert>
          )}
          {state.sent === true && (
            <Alert variant="success" canClose={false}>{t('page.contact.requestOk')}</Alert>
          )}
          {state.sent === false && (
            <form onSubmit={handleSubmit}>
              <div className={styles.fields}>
                <div>
                  <Input
                    error={errors.name}
                    name="name"
                    value={values.name}
                    type="text"
                    onChange={handleChange}
                    label={`${t('page.contact.yourName')} *`}
                    placeholder={t('userportal:page.contact.namePlaceholder')}
                  />
                </div>
                <div>
                  <Input
                    error={errors.email}
                    name="email"
                    value={values.email}
                    type="text"
                    onChange={handleChange}
                    label={`${t('form.email')} *`}
                    placeholder={t('userportal:page.contact.emailPlaceholder')}
                  />
                </div>
                <div>
                  <Input
                    error={errors.phone}
                    name="phone"
                    value={values.phone}
                    type="text"
                    onChange={handleChange}
                    label={t('form.phone')}
                    placeholder={t('userportal:page.contact.phonePlaceholder')}
                  />
                </div>
              </div>
              <Input
                error={errors.note}
                multiline
                rows={6}
                name="note"
                value={values.note}
                onChange={handleChange}
                label={`${t('page.contact.note')} *`}
                placeholder={t('userportal:page.contact.notePlaceholder')}
              />
              <div className={styles.formFooter}>
                <Submit
                  title={t('userportal:page.contact.sendMessage')}
                  disabled={hasError}
                  onClick={handleSubmit}
                  processing={state.processing}
                  noMargin
                />
                <span className={styles.responseTime}>{t('userportal:page.contact.responseTime')}</span>
              </div>
            </form>
          )}
        </div>
        <div className={styles.aside}>
          <div className={styles.personCard}>
            <img src="/images/josef.png" className={styles.photo} alt="Josef Zamrzla" />
            <div className={styles.personInfo}>
              <h3>Josef Zamrzla</h3>
              <span className={styles.role}>{t('page.contact.josefRole')}</span>
              <div className={styles.personLinks}>
                <a href="mailto:josef@skilleto.cz">josef@skilleto.cz</a>
                <a href="https://www.linkedin.com/in/josefzamrzla/" target="_blank" rel="noopener noreferrer">LinkedIn</a>
              </div>
            </div>
          </div>
          <div className={styles.contactsCard}>
            <span className={styles.cardKicker}>{t('page.contact.contacts')}</span>
            <div className={styles.company}>
              <strong>{t('owner.name')}</strong>
              <span>{t('owner.street')}, {t('owner.cityZip')}</span>
            </div>
            <div className={styles.cardDivider} />
            <div className={styles.detailsGrid}>
              <span className={styles.detailLabel}>{t('form.ic')}</span>
              <span>{t('owner.ic')}</span>
              <span className={styles.detailLabel}>{t('form.dic')}</span>
              <span>{t('owner.dic')}</span>
              <span className={styles.detailLabel}>{t('form.email')}</span>
              <a href={`mailto:${t('owner.email')}`}>{t('owner.email')}</a>
              <span className={styles.detailLabel}>{t('form.phone')}</span>
              <a href={`tel:${t('owner.phone')}`}>{t('owner.phone')}</a>
            </div>
          </div>
          <div className={styles.follow}>
            <span>{t('userportal:page.contact.followUs')}</span>
            <a href={BLOG_URL} target="_blank" rel="noopener noreferrer">blog.skilleto.cz</a>
            <span>·</span>
            <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer">LinkedIn</a>
            <span>·</span>
            <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer">Facebook</a>
          </div>
        </div>
      </div>
    </Page>
  );
};

export default Contact;
